import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useBookingWizard } from '@/contexts/BookingWizardContext';
import { supabase } from '@/integrations/supabase/client';
import LeadCaptureStep from './steps/LeadCaptureStep';
import PropertySelectionStep from './steps/PropertySelectionStep';
import VisitTypeStep from './steps/VisitTypeStep';

export type BookingStep = 'lead' | 'property' | 'visitType';

export interface BookingData {
  name: string;
  email: string;
  property: string;
  visitType: string;
}

const BookingWizardModal = () => {
  const { isOpen, closeWizard } = useBookingWizard();
  const [step, setStep] = useState<BookingStep>('lead');
  const [leadId, setLeadId] = useState<string | null>(null);
  const [leadToken, setLeadToken] = useState<string | null>(null);
  const [bookingData, setBookingData] = useState<BookingData>({
    name: '',
    email: '',
    property: '',
    visitType: '',
  });

  const handleLeadSubmit = async (name: string, email: string) => {
    setBookingData(prev => ({ ...prev, name, email }));
    
    // Save lead via edge function with rate limiting
    if (name && email) {
      try {
        const { data, error } = await supabase.functions.invoke('submit-booking-lead', {
          body: { action: 'create', name, email }
        });
        
        if (!error && data?.id) {
          setLeadId(data.id);
          setLeadToken(data.token ?? null);
        }
      } catch (err) {
        console.error('Failed to submit lead:', err);
      }
    }
    
    setStep('property');
  };

  const handleSkip = () => {
    setStep('property');
  };

  const handlePropertySelect = async (property: string) => {
    setBookingData(prev => ({ ...prev, property }));
    
    // Update lead via edge function
    if (leadId) {
      try {
        await supabase.functions.invoke('submit-booking-lead', {
          body: { action: 'update', leadId, leadToken, selectedProperty: property }
        });
      } catch (err) {
        console.error('Failed to update lead:', err);
      }
    }
    
    setStep('visitType');
  };

  const handleVisitTypeSelect = async (visitType: string) => {
    setBookingData(prev => ({ ...prev, visitType }));
    
    // Update lead via edge function
    if (leadId) {
      try {
        await supabase.functions.invoke('submit-booking-lead', {
          body: { action: 'update', leadId, leadToken, visitType }
        });
      } catch (err) {
        console.error('Failed to update lead:', err);
      }
    }
    
    // Redirect based on selection
    closeWizard();
    resetWizard();
    
    if (visitType === 'both') {
      window.location.href = 'https://www.sonsriverranch.com/book-now';
    } else if (visitType === 'day') {
      window.location.href = '/book-now?intent=day#sons-rio-cibolo';
    } else if (visitType === 'overnight') {
      window.location.href = '/book-now?intent=overnight#sons-rio-cibolo';
    }
  };

  const resetWizard = () => {
    setStep('lead');
    setLeadId(null);
    setBookingData({ name: '', email: '', property: '', visitType: '' });
  };

  const handleClose = () => {
    closeWizard();
    resetWizard();
  };

  return (
    <Dialog modal={false} open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-cream border-0">
        {step === 'lead' && (
          <LeadCaptureStep onSubmit={handleLeadSubmit} onSkip={handleSkip} />
        )}
        {step === 'property' && (
          <PropertySelectionStep onSelect={handlePropertySelect} />
        )}
        {step === 'visitType' && (
          <VisitTypeStep onSelect={handleVisitTypeSelect} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingWizardModal;
